import React, { useState } from 'react';
import { Building2, Plus, Lock, X, AlertTriangle, BookOpen, Loader2 } from 'lucide-react';
import { organizationAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

const COUNTRY_OPTIONS = [
  { code: 'CH', name: 'Switzerland', currency: 'CHF', chart: 'Kontenrahmen KMU', chartAccounts: 44 },
  { code: 'FR', name: 'France', currency: 'EUR', chart: 'Plan Comptable Général (PCG)', chartAccounts: 46 },
  { code: 'DE', name: 'Germany', currency: 'EUR', chart: null },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', chart: null },
  { code: 'US', name: 'United States', currency: 'USD', chart: null },
  { code: 'ES', name: 'Spain', currency: 'EUR', chart: null },
  { code: 'IT', name: 'Italy', currency: 'EUR', chart: null },
  { code: 'PT', name: 'Portugal', currency: 'EUR', chart: null },
  { code: 'NL', name: 'Netherlands', currency: 'EUR', chart: null },
  { code: 'BE', name: 'Belgium', currency: 'EUR', chart: null },
  { code: 'AT', name: 'Austria', currency: 'EUR', chart: null },
  { code: 'IE', name: 'Ireland', currency: 'EUR', chart: null },
  { code: 'SE', name: 'Sweden', currency: 'SEK', chart: null },
  { code: 'DK', name: 'Denmark', currency: 'DKK', chart: null },
  { code: 'NO', name: 'Norway', currency: 'NOK', chart: null },
  { code: 'FI', name: 'Finland', currency: 'EUR', chart: null },
  { code: 'PL', name: 'Poland', currency: 'PLN', chart: null },
  { code: 'CZ', name: 'Czech Republic', currency: 'CZK', chart: null },
  { code: 'CA', name: 'Canada', currency: 'CAD', chart: null },
  { code: 'AU', name: 'Australia', currency: 'AUD', chart: null },
  { code: 'JP', name: 'Japan', currency: 'JPY', chart: null },
  { code: 'IN', name: 'India', currency: 'INR', chart: null },
  { code: 'BR', name: 'Brazil', currency: 'BRL', chart: null },
  { code: 'SG', name: 'Singapore', currency: 'SGD', chart: null },
];

const COUNTRY_MAP = Object.fromEntries(COUNTRY_OPTIONS.map(c => [c.code, c]));

function OrgField({ label, value, placeholder = '—' }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-zinc-900">{value || <span className="text-zinc-300">{placeholder}</span>}</p>
    </div>
  );
}

export default function OrgTab({
  org, setOrg, editingOrg, setEditingOrg, orgForm, setOrgForm,
  processing, setProcessing, loadData,
  entities, switchEntity, canCreateEntity, maxEntities,
  setShowNewEntityDialog, fyCloseWizard, setFyCloseWizard,
  AutoApprovalToggle,
}) {
  const { user } = useAuth();
  const { refreshOrg } = useOrg();
  const [pendingCountryChange, setPendingCountryChange] = useState(null);

  const currentCountry = COUNTRY_MAP[org?.country];

  const handleSaveOrg = async () => {
    try {
      const res = await organizationAPI.update(orgForm);
      setOrg(res.data);
      setEditingOrg(false);
      toast.success('Organization updated');
      refreshOrg();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleCountrySelect = (code) => {
    if (!code || code === org?.country) {
      setOrgForm(f => ({ ...f, country_code: code }));
      return;
    }
    const target = COUNTRY_MAP[code];
    setPendingCountryChange(target);
  };

  const handleConfirmCountryChange = async () => {
    if (!pendingCountryChange) return;
    try {
      setProcessing(true);
      const res = await organizationAPI.changeCountry(pendingCountryChange.code);
      const data = res.data;
      toast.success(`${data.message} — ${data.accounts_seeded} accounts seeded`);
      setPendingCountryChange(null);
      setEditingOrg(false);
      await loadData();
      refreshOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change country');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-5 h-5 text-[#D94E18]" />Organization</CardTitle>
            <CardDescription>Manage your company identity and regional configuration.</CardDescription>
          </div>
          {!editingOrg ? (
            <Button variant="outline" size="sm" onClick={() => {
              setOrgForm({ company_name: org?.company_name || '', country: org?.country || '', currency: org?.currency || '', entity_type: org?.entity_type || 'business', vat_registered: org?.vat_registered || false });
              setEditingOrg(true);
            }} data-testid="edit-org-btn">Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditingOrg(false)}>Cancel</Button>
              <Button size="sm" className="bg-[#D94E18] hover:bg-[#C2410C]" onClick={handleSaveOrg} data-testid="save-org-btn">Save</Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {org ? (
            editingOrg ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Company Name</Label><Input value={orgForm.company_name || ''} onChange={e => setOrgForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Your company name" data-testid="org-company-name-input" /></div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <select
                    value={org?.country || ''}
                    onChange={(e) => handleCountrySelect(e.target.value)}
                    className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm"
                    data-testid="org-country-select"
                  >
                    <option value="">Select country...</option>
                    {COUNTRY_OPTIONS.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.currency}){c.chart ? ` — ${c.chart}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-zinc-400">Changing country updates chart of accounts, currency & tax config</p>
                </div>
                <div className="space-y-1.5"><Label>Entity Type</Label>
                  <select value={orgForm.entity_type || ''} onChange={e => setOrgForm(f => ({ ...f, entity_type: e.target.value }))} className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm" data-testid="org-entity-type-select">
                    <option value="personal">Personal</option><option value="business">Business</option>
                  </select>
                </div>
                {orgForm.entity_type === 'business' && (
                  <div className="space-y-1.5"><Label>VAT Registered</Label>
                    <label className="flex items-center gap-2 h-9 text-sm cursor-pointer">
                      <input type="checkbox" checked={orgForm.vat_registered} onChange={e => setOrgForm(f => ({ ...f, vat_registered: e.target.checked }))} className="rounded border-zinc-300" data-testid="org-vat-checkbox" />
                      VAT/Tax registered
                    </label>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <OrgField label="Company Name" value={org.company_name} placeholder="Not set" />
                <OrgField label="Country" value={currentCountry ? currentCountry.name : org.country} placeholder="Not set" />
                <OrgField label="Currency" value={org.currency} placeholder="Not set" />
                <OrgField label="Entity Type" value={org.entity_type ? org.entity_type.charAt(0).toUpperCase() + org.entity_type.slice(1) : null} />
                <OrgField label="VAT Registered" value={org.entity_type === 'business' ? (org.vat_registered ? 'Yes' : 'No') : 'N/A (Personal)'} />
                {currentCountry?.chart && <OrgField label="Chart of Accounts" value={currentCountry.chart} />}
              </div>
            )
          ) : (
            <p className="text-sm text-zinc-400 py-4">No organization data yet. Upload your first statement.</p>
          )}

          {org && (
            <div className="mt-6 p-4 border border-zinc-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-900">Auto-approve high-confidence transactions*</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Transactions classified with high AI confidence will be automatically posted during upload.</p>
                </div>
                <AutoApprovalToggle org={org} setOrg={setOrg} refreshOrg={refreshOrg} />
              </div>
            </div>
          )}

          {org && (
            <div className="mt-6 p-4 border border-zinc-200 rounded-lg" data-testid="ai-insights-section">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-900">Kaya proactive insights</p>
                  <p className="text-xs text-zinc-500 mt-0.5">When enabled, Kaya will surface spending insights and savings suggestions on your dashboard. Off by default.</p>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !org.ai_proactive_insights;
                    try {
                      await organizationAPI.update({ ai_proactive_insights: newVal });
                      setOrg({ ...org, ai_proactive_insights: newVal });
                      toast.success(newVal ? 'Proactive insights enabled' : 'Proactive insights disabled');
                    } catch { toast.error('Failed to update setting'); }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${org.ai_proactive_insights ? 'bg-[#D94E18]' : 'bg-zinc-300'}`}
                  data-testid="ai-insights-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${org.ai_proactive_insights ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          )}

          {org && (
            <div className="mt-6 p-4 border border-zinc-200 rounded-lg space-y-4" data-testid="fiscal-year-section">
              <div>
                <p className="text-sm font-medium text-zinc-900">Fiscal Year</p>
                <p className="text-xs text-zinc-500 mt-0.5">Configure your active fiscal year, start month, and lock completed years.</p>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Active Fiscal Year</Label>
                  <select
                    value={org.active_fiscal_year || new Date().getFullYear()}
                    onChange={async (e) => {
                      const year = parseInt(e.target.value);
                      try {
                        const res = await organizationAPI.update({ active_fiscal_year: year });
                        setOrg(res.data);
                        refreshOrg();
                        toast.success(`Active fiscal year set to ${year}`);
                      } catch { toast.error('Failed to update fiscal year'); }
                    }}
                    disabled={user?.role !== 'owner'}
                    className="h-9 rounded-md border border-zinc-200 px-3 text-sm font-mono"
                    data-testid="active-fiscal-year-select"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Start Month</Label>
                  <select
                    value={org.fiscal_year_start || 1}
                    onChange={async (e) => {
                      try {
                        const res = await organizationAPI.update({ fiscal_year_start: parseInt(e.target.value) });
                        setOrg(res.data);
                        refreshOrg();
                        toast.success('Fiscal year start updated');
                      } catch { toast.error('Failed to update'); }
                    }}
                    disabled={user?.role !== 'owner'}
                    className="h-9 rounded-md border border-zinc-200 px-3 text-sm"
                    data-testid="fiscal-year-start-select"
                  >
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, idx) => (
                      <option key={m} value={idx + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Locked Fiscal Years</Label>
                <div className="flex flex-wrap gap-2">
                  {(org.locked_fiscal_years || []).length > 0 ? (
                    (org.locked_fiscal_years || []).sort().map(year => (
                      <div key={year} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-sm">
                        <Lock className="w-3.5 h-3.5 text-red-500" />
                        <span className="font-medium text-red-700">{year}</span>
                        {user?.role === 'owner' && (
                          <button onClick={async () => {
                            try {
                              await organizationAPI.unlockFiscalYear(year);
                              const res = await organizationAPI.get();
                              setOrg(res.data);
                              toast.success(`FY ${year} unlocked`);
                            } catch { toast.error('Failed to unlock'); }
                          }} className="ml-1 text-red-400 hover:text-red-600" data-testid={`unlock-fy-${year}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <span className="text-xs text-zinc-400">No locked years</span>
                  )}
                </div>
                {user?.role === 'owner' && (
                  <div className="flex items-center gap-2 mt-2">
                    <select id="lock-fy-select" className="h-9 rounded-md border border-zinc-200 px-3 text-sm" data-testid="lock-fy-select">
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    <Button variant="outline" size="sm" onClick={async () => {
                      const year = parseInt(document.getElementById('lock-fy-select').value);
                      if ((org?.locked_fiscal_years || []).includes(year)) {
                        toast.error(`FY ${year} is already locked`);
                        return;
                      }
                      setFyCloseWizard({ open: true, year, step: 'preview', loading: true, preview: null, result: null });
                      try {
                        const res = await organizationAPI.previewClosingEntries(year);
                        setFyCloseWizard(prev => ({ ...prev, loading: false, preview: res.data }));
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to preview');
                        setFyCloseWizard({ open: false, year: null, step: 'preview', loading: false, preview: null, result: null });
                      }
                    }} data-testid="lock-fy-btn">
                      <Lock className="w-3.5 h-3.5 mr-1.5" />Close & Lock Year
                    </Button>
                  </div>
                )}
                {user?.role !== 'owner' && (
                  <p className="text-xs text-zinc-400">Only the Owner can lock/unlock fiscal years.</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chart of accounts info banner */}
      {!editingOrg && org?.country && currentCountry?.chart && (
        <Card className="border-blue-100 bg-blue-50/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-zinc-800">
                  Chart of Accounts: <span className="text-blue-700">{currentCountry.chart}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Government-mandated structure for {currentCountry.name}. {currentCountry.chartAccounts} bilingual accounts (native + English).
                </p>
              </div>
              <Badge variant="outline" className="ml-auto text-blue-700 border-blue-200 flex-shrink-0">{org.country}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legal Entities */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-5 h-5 text-[#D94E18]" />Legal Entities</CardTitle>
          <CardDescription>
            Manage multiple legal entities within your account.
            Your <strong>{org?.tier || 'free'}</strong> plan allows up to <strong>{maxEntities}</strong> {maxEntities === 1 ? 'entity' : 'entities'}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {entities.map(e => (
              <div key={e.id} className={`flex items-center justify-between p-3 rounded-lg border ${e.id === org?.id ? 'border-[#D94E18]/40 bg-orange-50/50' : 'border-zinc-200'}`} data-testid={`entity-row-${e.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${e.id === org?.id ? 'bg-[#D94E18]/10 text-[#D94E18]' : 'bg-zinc-100 text-zinc-500'}`}>
                    {(e.company_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{e.company_name}</p>
                    <p className="text-xs text-zinc-400">{e.country_code} &middot; {e.currency}</p>
                  </div>
                </div>
                {e.id === org?.id ? (
                  <Badge className="bg-[#D94E18] text-white text-[10px]">Active</Badge>
                ) : (
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                    const ok = await switchEntity(e.id);
                    if (ok) { toast.success(`Switched to ${e.company_name}`); window.location.href = '/dashboard'; }
                  }} data-testid={`switch-entity-${e.id}`}>Switch</Button>
                )}
              </div>
            ))}
          </div>
          {canCreateEntity && user?.role === 'owner' && (
            <Button variant="outline" className="mt-3 w-full" onClick={() => setShowNewEntityDialog(true)} data-testid="settings-create-entity-btn">
              <Plus className="w-4 h-4 mr-1.5" />Add Legal Entity
            </Button>
          )}
          {!canCreateEntity && (
            <p className="text-xs text-zinc-400 mt-3 text-center">Entity limit reached. Upgrade your plan to add more.</p>
          )}
        </CardContent>
      </Card>

      {/* Country change confirmation dialog */}
      <AlertDialog open={!!pendingCountryChange} onOpenChange={() => setPendingCountryChange(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />Change Country to {pendingCountryChange?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>This will update your organization&apos;s regional configuration:</p>
                <ul className="space-y-1.5 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">1.</span>
                    <span><strong>Chart of Accounts</strong> — {pendingCountryChange?.chart
                      ? `Replace with ${pendingCountryChange.chart} (${pendingCountryChange.chartAccounts} bilingual accounts)`
                      : 'Seed generic international chart'
                    }</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">2.</span>
                    <span><strong>Tax/VAT Configuration</strong> — Set up {pendingCountryChange?.name}&apos;s tax rates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-600 font-bold mt-0.5">3.</span>
                    <span><strong>Currency</strong> — Switch to {pendingCountryChange?.currency}</span>
                  </li>
                </ul>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 font-medium">
                  If you have posted journal entries, existing accounts will be kept and new ones added. Otherwise, the chart will be fully replaced.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCountryChange}
              disabled={processing}
              className="bg-[#D94E18] hover:bg-[#C2410C]"
              data-testid="confirm-country-change-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Change to {pendingCountryChange?.name}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
